import { useTranslation } from "react-i18next";
import { Manager } from "../../../Manager";
import {
  fetchStoreSets,
  hasStoreSetMarketingActivities,
  updateStoreSetStores,
  type StoreSet,
} from "../../../../state/store-sets";
import {
  searchStores,
  fetchStoresByIds,
  type Store,
} from "../../../../state/stores";
import { supabase } from "../../../../lib/supabase";
import type { SearchParams, SortParams } from "../../../Manager/types";
import { useCallback } from "react";
import { Box, Callout } from "@radix-ui/themes";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { useManagerContext } from "../../../Manager/context";
import type { StoreSet as StoreSetType } from "../../../../state/store-sets";

const SEARCH_COLUMNS = ["name"];

/**
 * Shows a warning when editing an existing store set about store composition changes.
 */
function StoreSetCompositionWarning({ t }: { t: (key: string) => string }) {
  const { isEditMode } = useManagerContext<StoreSetType>();
  if (!isEditMode) return null;
  return (
    <Callout.Root color="amber" size="1">
      <Callout.Icon>
        <InfoCircledIcon />
      </Callout.Icon>
      <Callout.Text>{t("storeSets.compositionWarning")}</Callout.Text>
    </Callout.Root>
  );
}

export function ManageStoreSets() {
  const { t } = useTranslation();
  const fetchData = useCallback(
    (
      limit?: number,
      offset?: number,
      searchParams?: SearchParams,
      sortParams?: SortParams,
    ) => fetchStoreSets(limit, offset, searchParams, sortParams),
    [],
  );

  const handleSubmit = async (
    data: Record<string, unknown>,
    item: StoreSet | null,
  ) => {
    const name = data.name as string;
    const storeIds = (data.store_ids as number[]) ?? [];

    if (item) {
      // Update name
      const { error } = await supabase
        .from("store_sets")
        .update({ name })
        .eq("id", item.id);
      if (error) throw new Error(error.message);

      // Update stores
      await updateStoreSetStores(item.id, storeIds);
    } else {
      // Create store set
      const { data: newStoreSet, error } = await supabase
        .from("store_sets")
        .insert([{ name }])
        .select("id")
        .single();
      if (error) throw new Error(error.message);

      // Link stores
      if (storeIds.length > 0) {
        await updateStoreSetStores(newStoreSet.id, storeIds);
      }
    }
  };

  const handleDelete = async (item: StoreSet) => {
    // Check if there are marketing activities attached
    const hasActivities = await hasStoreSetMarketingActivities(item.id);
    if (hasActivities) {
      throw new Error(t("storeSets.deleteError"));
    }

    // Delete store links first
    const { error: linksError } = await supabase
      .from("stores_store_sets")
      .delete()
      .eq("store_set_id", item.id);
    if (linksError) throw new Error(linksError.message);

    // Delete store set
    const { error } = await supabase
      .from("store_sets")
      .delete()
      .eq("id", item.id);
    if (error) throw new Error(error.message);
  };

  const validateForm = (data: Record<string, unknown>): boolean => {
    const name = data.name as string;
    if (!name?.trim()) return false;
    return true;
  };

  return (
    <Box p="4">
      <Manager.Root<StoreSet>
        title={t("storeSets.title")}
        fetchData={fetchData}
      >
        <Manager.Sort
          columns={[
            { key: "name", isDefault: true, defaultOrder: "asc" },
            { key: "store_count" },
          ]}
        />
        <Manager.Search>
          <Manager.TextSearch
            columns={SEARCH_COLUMNS}
            placeholder={t("storeSets.searchPlaceholder")}
          />
        </Manager.Search>

        <Manager.CreateButton label={t("storeSets.createStoreSet")} />

        <Manager.Table>
          <Manager.Column<StoreSet>
            header={t("storeSets.name")}
            accessor={(s) => s.name}
            sortKey="name"
          />
          <Manager.Column<StoreSet>
            header={t("storeSets.storeCount")}
            accessor={(s) => s.store_count}
            sortKey="store_count"
          />
          <Manager.Actions>
            <Manager.EditAction />
            <Manager.DeleteAction />
          </Manager.Actions>
        </Manager.Table>

        <Manager.FormDialog<StoreSet>
          title={(isEdit) =>
            isEdit ? t("storeSets.editStoreSet") : t("storeSets.createStoreSet")
          }
          description={(isEdit) =>
            isEdit
              ? t("storeSets.editDescription")
              : t("storeSets.createDescription")
          }
          onSubmit={handleSubmit}
          validate={validateForm}
        >
          <Manager.TextField
            name="name"
            label={t("storeSets.name")}
            required
            placeholder={t("storeSets.namePlaceholder")}
            getValue={(item) => (item as StoreSet).name}
          />
          <Manager.MultiAutocompleteField<Store>
            name="store_ids"
            label={t("storeSets.stores")}
            placeholder={t("storeSets.storesPlaceholder")}
            getValue={(item) => {
              // This is async, but we need to handle it differently
              // The form field will use fetchByIdsFn to load the initial values
              const storeSet = item as StoreSet;
              return storeSet.stores_store_sets?.map((s) => s.store_id) ?? [];
            }}
            searchFn={searchStores}
            fetchByIdsFn={fetchStoresByIds}
            getOptionLabel={(store) => store.name}
            getOptionValue={(store) => store.id}
          />
          <StoreSetCompositionWarning t={t} />
        </Manager.FormDialog>

        <Manager.DeleteDialog<StoreSet>
          getItemName={(s) => s.name}
          onDelete={handleDelete}
        />
      </Manager.Root>
    </Box>
  );
}
